/** * メッセージカードコンポーネント */
<template>
  <div class="card-message">
    <!-- 送信者 -->
    <card-user
      can-user-name-click
      v-bind:user-id="sendUserId"
      v-bind:user-name="sendUserName"
      v-bind:groups="groups"
      v-bind:profile-image-attach-file-type="sendUserProfileAttachFileType"
      v-on:usercard-username-click="
        $store.dispatch('updateProfileUserId', $event)
      "
    />
    <b-card-text
      align="left"
      v-on:click="$_onClickCard"
      class="event-card-body"
      v-bind:class="{ 'is-unread': isUnread }"
    >
      <div class="event-card-body-left">
        <!-- メッセージタイトル -->
        <div class="event-card-title">
          <div class="title-wrapper">
            <icon-message class="icon-message" />
            <p class="title">
              {{ title }}
            </p>
            <span v-if="existsAttachFiles" class="attach-file">
              {{ attachFilesCount }}
            </span>
          </div>
          <p class="time">
            {{ sendAtText }}
          </p>
        </div>
        <!-- メッセージ本文 -->
        <p class="event-card-description">
          {{ content }}
        </p>
        <data-list v-if="scheduleTitle" v-bind:data="dataList" />
      </div>
      <!-- <div class="event-card-body-right"></div> -->
    </b-card-text>
  </div>
</template>

<script>
import { toRef } from 'vue'
import DataList from '@/components/AppDataList.vue'
import CardUser from '@/components/AppCardUser.vue'
import IconMessage from '@/assets/ifield/ic_message.svg?component'
import { useDateDisplayText } from '@/composables/UseDateDisplayText.js'

/**
 * タイムラインタイプ（機能のタイプ)
 * @type {number}
 */
const TIMELINE_TYPE = 3

const MESSAGE_TYPE = {
  RECEIVE: 'receive',
  SEND: 'send',
}

export default {
  name: 'AppCardMessage',
  components: {
    'data-list': DataList,
    'card-user': CardUser,
    'icon-message': IconMessage,
  },
  props: {
    /**
     * メッセージタイプ(receive|send)
     */
    messageType: {
      type: String,
      default: MESSAGE_TYPE.RECEIVE,
      required: false,
      validator: function (value) {
        return [MESSAGE_TYPE.RECEIVE, MESSAGE_TYPE.SEND].indexOf(value) !== -1
      },
    },
    /**
     * ID
     * (データを一意に識別することができるID。タイムラインID、メッセージID)
     */
    id: {
      type: Number,
      default: null,
      required: true,
    },
    /**
     * メッセージ本文
     */
    content: {
      type: String,
      default: null,
      required: true,
    },
    /**
     * スケジュールタイトル
     */
    scheduleTitle: {
      type: String,
      default: null,
      required: false,
    },
    /**
     * メッセージ既読フラグ
     * (既読の場合はtrue)
     */
    isRead: {
      type: Boolean,
      default: null,
      required: false,
    },
    /**
     * 添付ファイル数
     */
    attachFilesCount: {
      type: Number,
      default: null,
      required: true,
    },
    /**
     * 送信者ユーザーID
     */
    sendUserId: {
      type: Number,
      default: null,
      required: true,
    },
    /**
     * 送信者名
     */
    sendUserName: {
      type: String,
      default: null,
      required: true,
    },
    /**
     * 送信者のユーザープロファイルのファイルタイプ
     */
    sendUserProfileAttachFileType: {
      type: String,
      default: '9',
    },
    /**
     * 送信日時
     */
    sendAt: {
      type: String,
      default: null,
      required: true,
    },
    /**
     * 基準日時
     */
    baseDate: {
      type: Date,
      default: null,
    },
    /**
     * 送信者の所属グループ
     */
    groups: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const { text } = useDateDisplayText({
      date: toRef(props, 'sendAt'),
      baseDate: toRef(props, 'baseDate'),
    })
    return { sendAtText: text }
  },
  computed: {
    /**
     * 送信日時
     * @type {Date}
     */
    sendDate: function () {
      return this.sendAt ? new Date(this.sendAt) : null
    },
    /**
     * カードタイトル
     * @return {string}
     */
    title: function () {
      if (this.messageType === MESSAGE_TYPE.SEND) {
        return this.$t('appCardMessage.sentMessage')
      }
      return this.$t('appCardMessage.receivedMessage')
    },

    /**
     * 添付ファイルが存在している場合はtrueを返却する
     * @return {boolean}
     */
    existsAttachFiles: function () {
      return this.attachFilesCount > 0
    },

    /**
     * DataListコンポーネントに設定するデータを取得する
     */
    dataList: function () {
      return {
        standard: [
          {
            label: this.$t('appCardMessage.field.task'),
            value: this.scheduleTitle,
          },
        ],
      }
    },

    /**
     * 未読メッセージかどうか
     * @return {boolean} 未読メッセージの場合true、既読メッセージや送信メッセージはfalse
     */
    isUnread: function () {
      if (this.messageType === MESSAGE_TYPE.RECEIVE && !this.isRead) {
        return true
      }
      return false
    },
  },
  methods: {
    /**
     * クリック時のイベント
     */
    $_onClickCard: function () {
      this.$emit('messagecard-click', TIMELINE_TYPE, this.id)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .card-user {
  padding-right: 8px;
}

.event-card-body {
  background-color: #f6f6f6;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  height: auto;
  padding: 0;
  margin: 10px 0 0 0;
  border-radius: 2px;
  overflow: hidden;
  transition: 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
  }

  &.is-unread {
    background-color: #fcfadd;
    &:hover {
      background-color: #f9f7cf;
    }
  }
}

.event-card-body-left {
  width: 100%;
  height: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 16px;
}

/* .event-card-message-body-right {
  width: 100px;
  height: 100%;
} */

.event-card-title {
  width: 100%;
}

.title-wrapper {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: baseline;
  margin-bottom: 8px;

  .icon-message {
    flex-shrink: 0;
    height: 20px;
    width: 20px;
    padding: 3px;
    margin-right: 12px;
    border-radius: 50%;
    position: relative;
    top: 4px;
    fill: $entity-color-white;
    background-color: $entity-color-green;
  }

  .title {
    font-size: 0.9em !important;
    max-width: calc(100% - 75px);
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .attach-file {
    flex-shrink: 0;
    color: #808080;
    margin-left: 10px;
    font-size: 0.9em;

    &:before {
      content: '';
      display: inline-block;
      width: 16px;
      height: 16px;
      margin-right: 2px;
      background: url('../../assets/ifield/icon_attach_file.png') no-repeat;
      background-size: 16px 16px;
      position: relative;
      top: 2px;
    }
  }
}

.time {
  color: #959595;
  font-size: $font-size-small;
  line-height: 1;
  margin: 0;
}

.event-card-description {
  margin-bottom: 0;
  color: #4d4d4d;
  font-size: 0.8em;
  line-height: 1.4;
  max-height: 52px;

  @supports (-webkit-line-clamp: 3) {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
}

::v-deep .data-list {
  .list-wrapper {
    margin-bottom: 0;
    li {
      margin-bottom: 0;
    }
  }
}
</style>
